/* eslint-disable max-lines */
import Icon from 'components/Input/Icon';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { fontSizePalette, fontWeightPalette, sizePalette } from 'utils/styleConstants';

const borderMixin = (theme: any) => css`
  border-bottom: solid ${sizePalette.borderWidth.DEFAULT} ${theme.colorPalette.grey.grey5};
  &:first-child {
    border-left: solid ${sizePalette.borderWidth.DEFAULT} ${theme.colorPalette.grey.grey5};
  }
  &:last-child {
    border-right: solid ${sizePalette.borderWidth.DEFAULT} ${theme.colorPalette.grey.grey5};
  }
`;

const fixedColumnMixin = (theme: any) => css`
  position: sticky;
  z-index: ${theme.zIndexPalette.high};
  background-color: ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey1 : theme.colorPalette.grey.grey4};
  &.last-fixed {
    border-right: solid ${sizePalette.borderWidth.DEFAULT} ${theme.colorPalette.grey.grey5};
  }
`;

const dimensionMixin = css<{ width?: string | number; left?: string | number }>`
  ${({ width }) =>
    width &&
    css`
      width: ${width}px;
    `}
  ${({ left }) =>
    (left || left === 0) &&
    css`
      left: ${left}px;
    `}
`;
const TableContainer = styled.div`
  padding-bottom: ${sizePalette.spacing[2]};
  overflow-y: auto;
`;
// Header border styles based on light mode, header visibility, and theme
const headerBorderStyles = (light: boolean, hideHeader: boolean, theme: any) => {
  if (light && !hideHeader) {
    return css`
      border-bottom: solid ${sizePalette.borderWidth.DEFAULT} ${theme.colorPalette.grey.grey5};
      background-color: ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey3 : theme.colorPalette.white};
    `;
  }

  if (!light) {
    return css`
      border-top: ${hideHeader
        ? 'none'
        : `solid 1px ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey5}`};
      background-color: ${theme.colorPalette.isDarkMode
        ? theme.colorPalette.grey.grey3
        : theme.colorPalette.grey.grey2};

      &:first-child {
        border-top-left-radius: 3px;
        border-left: solid 1px
          ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey5};
      }
      &:last-child {
        border-top-right-radius: 3px;
        border-right: solid 1px
          ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey5};
      }
    `;
  }
  return '';
};

const StyledTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.grey.grey9};
  font-size: ${fontSizePalette.xSmall};
  text-align: left;
  user-select: none;
  white-space: nowrap;
`;

const THead = styled.thead<{ light: boolean; hideHeader: boolean }>`
  position: sticky;
  top: ${sizePalette.spacing[0]};
  font-weight: ${fontWeightPalette.medium};
  z-index: ${({ theme }) => theme.zIndexPalette.medium};

  th {
    margin: ${sizePalette.spacing[0]};

    padding: ${({ hideHeader }) => (hideHeader ? sizePalette.spacing[0] : sizePalette.spacing[2.5])};
    height: ${({ hideHeader }) => (hideHeader ? sizePalette.spacing[0] : null)};
    ${({ light, hideHeader, theme }) => headerBorderStyles(light, hideHeader, theme)}

    .thLabel {
      height: ${sizePalette.size.full};
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }

    &.fixed {
      ${({ theme }) => fixedColumnMixin(theme)}
    }
  }
`;

// Body styles with updated hover and selection colors
const rowStateStyles = (theme: any) => css`
  /*opacity wont work here so i copied direcly the color with the opacity */
  &.row-selected td {
    background-color: ${theme.colorPalette.isDarkMode ? '#4D4E4F' : '#E0F0F2'};
  }

  &.row-disabled td {
    color: ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey5 : theme.colorPalette.grey.grey4};
    cursor: default;
  }
  &:not(.row-disabled):hover:not(.row-selected):not(.edited-row) td {
    background-color: ${theme.colorPalette.isDarkMode ? '#5d5e60' : '#C1E1E4'};
  }
`;

const firstRowStyles = (hideHeader: boolean, light: boolean, theme: any) => {
  if (hideHeader && !light) {
    return css`
      &:first-child {
        td {
          border-top: solid ${sizePalette.borderWidth.DEFAULT} ${theme.colorPalette.grey.grey5};
        }
        td:first-child {
          border-top-left-radius: ${sizePalette.borderRadius.DEFAULT};
        }
        td:last-child {
          border-top-right-radius: ${sizePalette.borderRadius.DEFAULT};
        }
      }
    `;
  }
  return '';
};

const TBody = styled.tbody<{ light: boolean; hideHeader: boolean; headerHeight?: string }>`
  position: relative;
  font-weight: ${fontWeightPalette.light};

  @-moz-document url-prefix() {
    z-index: ${({ theme }) => theme.zIndexPalette.high};
    position: absolute;
    top: ${({ headerHeight }) => headerHeight};
    left: ${sizePalette.spacing[0]};
  }

  tr {
    background-color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey2 : theme.colorPalette.white};
    user-select: none;
    cursor: pointer;
    display: table;

    ${({ theme }) => rowStateStyles(theme)}
    ${({ hideHeader, light, theme }) => firstRowStyles(hideHeader, light, theme)}

    &:last-child {
      td:first-child {
        border-bottom-left-radius: ${sizePalette.borderRadius.DEFAULT};
      }

      td:last-child {
        border-bottom-right-radius: ${sizePalette.borderRadius.DEFAULT};
      }
    }

    &:not(.row-selected) td.fixed {
      background-color: ${({ theme }) =>
        theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey2 : theme.colorPalette.white};
    }

    td {
      height: ${sizePalette.spacing.pxToRem(40)};
      display: flex;
      align-items: center;

      ${({ light, theme }) => !light && borderMixin(theme)}

      &.fixed {
        position: sticky;

        &.last-fixed {
          border-right: solid ${sizePalette.borderWidth.DEFAULT} ${({ theme }) => theme.colorPalette.grey.grey5};
        }
      }
    }
  }
`;

const TD = styled.td<{ width?: string | number; left?: string | number }>`
  ${dimensionMixin}
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  > div {
    display: flex;
    align-items: center;
    width: ${sizePalette.size.full};
    height: ${sizePalette.size.full};
    padding: ${sizePalette.spacing[0]} ${sizePalette.spacing[2]};
  }
`;

const TH = styled.th<{ width?: number | 'auto' | string; left?: number }>`
  ${dimensionMixin}
  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;
const Resizer = styled.div<{ lastItem?: boolean }>`
  width: 15px;
  border-right: solid 1px ${({ lastItem, theme }) => (lastItem ? 'transparent' : theme.colorPalette.grey.grey5)};
  height: 100%;
  position: absolute;
  top: ${sizePalette.spacing[0]};
  right: ${sizePalette.spacing[0]};

  &:hover {
    cursor: col-resize;
  }
`;

const Sorter = ({ isSorted, isSortedDesc }: { isSorted: boolean; isSortedDesc?: boolean }) => {
  return isSorted ? <Icon icon={isSortedDesc ? 'arrow-down' : 'arrow-up'} size="16px" /> : null;
};

const Filter = styled.div`
  padding-bottom: ${sizePalette.spacing[2.5]};
`;

const BottomContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${sizePalette.spacing[0]} ${sizePalette.spacing[1]};
`;

const StyledCheckboxWrapper = styled.div``;
const StyledHeadCheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: ${sizePalette.size.full};
  justify-content: center;
`;
const Circle = styled.span`
  height: ${sizePalette.spacing[4]};
  width: ${sizePalette.spacing[4]};
  border-radius: 50%;
  background-color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.light : theme.colorPalette.turquoise.dark};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: clamp(${sizePalette.spacing[1.5]}, ${fontSizePalette.xxSmall}, ${sizePalette.spacing[4]});
  margin-left: ${sizePalette.spacing[1]};
  padding: ${sizePalette.spacing[1]};
  color: ${({ theme }) => (theme.colorPalette.isDarkMode ? theme.colorPalette.black : theme.colorPalette.white)};
  align-content: center;
`;

const ToolbarTableContainer = styled.div`
  max-width: fit-content;
`;

const StyledArrow = styled.div<{ width?: string; height?: string; top?: string }>`
  cursor: pointer;
  width: ${({ width }) => width || sizePalette.spacing.pxToRem(28)};
  height: ${({ height }) => height || sizePalette.spacing[6]};
  position: absolute;
  top: ${({ top }) => top || sizePalette.spacing.pxToRem(36)};
  left: ${sizePalette.spacing[0]};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;
export {
  TableContainer,
  StyledTable,
  THead,
  TH,
  TD,
  TBody,
  Resizer,
  Sorter,
  Filter,
  BottomContainer,
  StyledCheckboxWrapper,
  StyledHeadCheckboxWrapper,
  Circle,
  ToolbarTableContainer,
  StyledArrow,
};
